import React, { useState, useRef, useEffect } from 'react';
import { ImagePlus } from 'lucide-react';
import apiClient from '../services/apiClient';
import './CoverUploadWidget.css';

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB
const ACCEPT_EXTENSIONS = '.jpg,.jpeg,.png,.webp';

function CoverUploadWidget({
  noteId,
  currentCoverUrl,
  currentCoverPreview = null,
  labels = {},
  onUploadComplete,
  onRemove,
  onFileSelect,
  enableGallery = false,
  onGallerySelect,
  isUploading = false,
  uploadProgress = 0,
  uploadError = null,
}) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [error, setError] = useState(null);
  const [activeSource, setActiveSource] = useState('upload');
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryError, setGalleryError] = useState(null);
  const [galleryLoaded, setGalleryLoaded] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewUrl && !previewUrl.startsWith('http') && !previewUrl.startsWith('/')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!enableGallery || activeSource !== 'gallery' || galleryLoaded) {
      return undefined;
    }

    let isMounted = true;
    setGalleryLoading(true);
    setGalleryError(null);

    apiClient.getCoverGallery()
      .then((images) => {
        if (!isMounted) return;
        setGalleryImages(images);
        setGalleryLoaded(true);
      })
      .catch((err) => {
        if (!isMounted) return;
        setGalleryError(err.message || 'Failed to load gallery images');
      })
      .finally(() => {
        if (isMounted) {
          setGalleryLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [activeSource, enableGallery, galleryLoaded]);

  const isValidType = (file) => ALLOWED_TYPES.includes(file.type);
  const isValidSize = (file) => file.size <= MAX_FILE_SIZE;

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setError(null);

    if (!file) {
      return;
    }

    if (!isValidType(file)) {
      setError(labels.invalidType || 'Only JPEG, PNG, and WebP files are accepted');
      resetFileInput();
      return;
    }

    if (!isValidSize(file)) {
      setError(labels.tooLarge || 'File exceeds the maximum size of 5 MB');
      resetFileInput();
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);

    if (onFileSelect) {
      onFileSelect(file);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    setError(null);
    resetFileInput();
    if (onFileSelect) {
      onFileSelect(null);
    }
    if (onRemove) {
      onRemove();
    }
  };

  const handleGallerySelect = async (image) => {
    if (!noteId || !image?.filename) return;

    setError(null);
    setGalleryError(null);

    try {
      const result = await apiClient.applyGalleryCover(noteId, image.filename);
      setPreviewUrl(null);
      resetFileInput();

      if (onUploadComplete) {
        onUploadComplete(result.coverUrl, result);
      }
      if (onGallerySelect) {
        onGallerySelect(result.coverUrl, image);
      }
    } catch (err) {
      setGalleryError(err.message || 'Failed to select gallery image');
    }
  };

  const displayUrl = previewUrl || currentCoverUrl;
  const hasPreview = Boolean(displayUrl || currentCoverPreview);
  const safeUploadProgress = Math.min(100, Math.max(0, Math.round(Number(uploadProgress) || 0)));
  const displayedError = error || uploadError;

  return (
    <div className={`cover-upload-widget ${isUploading ? 'is-uploading' : ''}`} data-testid="cover-upload-widget">
      {enableGallery && (
        <div className="cover-source-tabs" role="tablist" aria-label="Cover image source">
          <button
            type="button"
            className={`cover-source-tab ${activeSource === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveSource('upload')}
            disabled={isUploading}
            role="tab"
            aria-selected={activeSource === 'upload'}
          >
            Upload
          </button>
          <button
            type="button"
            className={`cover-source-tab ${activeSource === 'gallery' ? 'active' : ''}`}
            onClick={() => setActiveSource('gallery')}
            disabled={isUploading}
            role="tab"
            aria-selected={activeSource === 'gallery'}
          >
            App Gallery
          </button>
        </div>
      )}

      {hasPreview && (
        <div className="cover-preview-container">
          {displayUrl ? <img
            src={displayUrl}
            alt={labels.preview || 'Cover preview'}
            className="cover-preview-image"
            data-testid="cover-preview"
          /> : currentCoverPreview}
        </div>
      )}

      {activeSource === 'upload' && !hasPreview && (
        <label
          className="cover-upload-area"
          htmlFor={`cover-input-${noteId}`}
          data-testid="cover-upload-area"
        >
          <span className="cover-upload-icon" aria-hidden="true"><ImagePlus size={28} /></span>
          <span className="cover-upload-text">{labels.add || 'Click to add a cover image'}</span>
          <span className="cover-upload-hint">{labels.formats || 'JPEG, PNG, or WebP - max 5 MB'}</span>
        </label>
      )}

      {activeSource === 'upload' && hasPreview && (
        <div className="cover-preview-actions">
          <label
            className={`cover-btn cover-btn-change ${isUploading ? 'disabled' : ''}`}
            htmlFor={isUploading ? undefined : `cover-input-${noteId}`}
          >
            {labels.change || 'Change'}
          </label>
          <button
            type="button"
            className="cover-btn cover-btn-remove"
            onClick={handleRemove}
            disabled={isUploading}
            data-testid="cover-remove-btn"
          >
            {labels.remove || 'Remove'}
          </button>
        </div>
      )}

      {enableGallery && activeSource === 'gallery' && (
        <div className="cover-gallery-panel" data-testid="cover-gallery-panel">
          {galleryLoading && (
            <div className="cover-gallery-state">Loading gallery...</div>
          )}

          {!galleryLoading && galleryError && (
            <div className="cover-error" role="alert" data-testid="cover-gallery-error">
              <span className="cover-error-text">{galleryError}</span>
            </div>
          )}

          {!galleryLoading && !galleryError && galleryImages.length === 0 && (
            <div className="cover-gallery-state">No gallery images yet.</div>
          )}

          {!galleryLoading && !galleryError && galleryImages.length > 0 && (
            <div className="cover-gallery-grid">
              {galleryImages.map((image) => (
                <button
                  key={image.filename}
                  type="button"
                  className={`cover-gallery-tile ${currentCoverUrl === image.url ? 'active' : ''}`}
                  onClick={() => handleGallerySelect(image)}
                  disabled={isUploading}
                  title={image.displayName}
                >
                  <img
                    src={image.thumbnailUrl}
                    alt={image.displayName}
                    className="cover-gallery-thumbnail"
                    loading="lazy"
                  />
                  <span className="cover-gallery-name">{image.displayName}</span>
                </button>
              ))}
            </div>
          )}

          {displayUrl && (
            <div className="cover-preview-actions">
              <button
                type="button"
                className="cover-btn cover-btn-remove"
                onClick={handleRemove}
                disabled={isUploading}
                data-testid="cover-remove-btn"
              >
                Remove
              </button>
            </div>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        id={`cover-input-${noteId}`}
        type="file"
        accept={ACCEPT_EXTENSIONS}
        onChange={handleFileChange}
        disabled={isUploading}
        className="cover-file-input"
        data-testid="cover-file-input"
        aria-label={labels.select || 'Select cover image'}
      />

      {isUploading && (
        <div className="cover-upload-progress" role="status" aria-live="polite" data-testid="cover-upload-progress">
          <div className="cover-upload-progress-row">
            <span>Uploading cover image...</span>
            <strong>{safeUploadProgress}%</strong>
          </div>
          <div className="cover-upload-progress-track" aria-hidden="true">
            <div className="cover-upload-progress-fill" style={{ width: `${safeUploadProgress}%` }} />
          </div>
        </div>
      )}

      {displayedError && (
        <div className="cover-error" role="alert" data-testid="cover-error">
          <span className="cover-error-icon" aria-hidden="true">!</span>
          <span className="cover-error-text">{displayedError}</span>
        </div>
      )}
    </div>
  );
}

export { ALLOWED_TYPES, MAX_FILE_SIZE };
export default CoverUploadWidget;
